import { isNull } from 'components/app/_helpers/_app.helper';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { TsPayload } from './timestamp.slice';

export const MIN_STEP = 0.01;

export const linkedActions = (clip: Clip, playerTime: EditorTime, payload: TsPayload) => {
  const { minTime, maxTime, leftNeighbor, rightNeighbor, isLastInGroup, isFirstInGroup } = payload;

  const canSetStartTime = () => {
    if (clip.config.speed !== 1 && clip.endTime) return false;
    if (playerTime === clip.startTime) return false;
    if (isFirstInGroup || clip.startTime) return playerTime > minTime && playerTime < (clip.endTime ?? maxTime);
    return playerTime === leftNeighbor!.endTime;
  };
  const canSetEndTime = () => {
    if (clip.config.speed !== 1 && clip.startTime) return false;
    if (playerTime === clip.endTime) return false;
    if (isLastInGroup || clip.endTime) return playerTime > (clip.startTime ?? minTime) && playerTime < maxTime;
    return playerTime === rightNeighbor!.startTime;
  };
  const canSplit = () => {
    if (isNull(clip.endTime)) return playerTime > clip.startTime! && playerTime < maxTime;
    if (isNull(clip.startTime)) return playerTime > minTime && playerTime < clip.endTime;
    return playerTime > clip.startTime && playerTime < clip.endTime;
  };
  const canRemoveStartTime = () => {
    if (isNull(clip.startTime) || isNull(clip.endTime)) return false;
    if (isFirstInGroup) return true;
    return !!leftNeighbor!.endTime;
  };
  const canRemoveEndTime = () => {
    if (isNull(clip.startTime) || isNull(clip.endTime)) return false;
    if (isLastInGroup) return true;
    return !!rightNeighbor!.startTime;
  };
  return { canSetStartTime, canSetEndTime, canSplit, canRemoveEndTime, canRemoveStartTime };
};

export const getTsSliderActions = (
  clip: Clip,
  linkedPayload: TsPayload | null,
  playerDuration: Seconds,
  beginnerMode: boolean
) => {
  let start = false;
  let end = false;
  if (linkedPayload) {
    const actions = linkedActions(clip, getAverageTime(clip, linkedPayload), linkedPayload);
    start = actions.canSetStartTime();
    end = actions.canSetEndTime();
  } else {
    start = clip.config.speed === 1 || !clip.endTime;
    end = clip.config.speed === 1 || !clip.startTime;
  }
  if (!beginnerMode) return { start, end };

  const initData = getTsSliderInitData(clip, linkedPayload, playerDuration);
  if (initData.end === playerDuration) end = false;
  if (initData.start === null) start = false;
  return { start, end };
};

export const getTsSliderInitData = (clip: Clip, linkedPayload: TsPayload | null, playerDuration: Seconds) => {
  return linkedPayload
    ? {
        start: clip.startTime ?? linkedPayload.minTime,
        end: clip.endTime ?? linkedPayload.maxTime,
        min: linkedPayload.minTime + MIN_STEP,
        max: linkedPayload.maxTime - MIN_STEP,
      }
    : {
        start: clip.startTime!,
        end: clip.endTime ?? playerDuration,
        max: playerDuration,
        min: 0,
      };
};

const getAverageTime = (clip: Clip, payload: TsPayload) => {
  const { minTime, maxTime } = payload;
  const startTime = clip.startTime ?? minTime;
  const endTime = clip.endTime ?? maxTime;
  return startTime + (endTime - startTime) / 2;
};
